import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { ImageUploadArea } from "./ImageUpload";
import { Checkbox } from "@/lib/components/Checkbox";
import GradientTabButton from "@/lib/components/Button/GradientTabButton";
import {
  IconPhoto,
  IconPlus,
  IconTag,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { cn } from "@/lib/utils";
import { Button } from "@/lib/components/Button/Button";
import { DialogTrigger } from "@radix-ui/react-dialog";
import {
  useDeleteAssetMediasMutation,
  useTeamAssetMediasQuery,
  useUploadAssetMediasMutation,
  assetMediaQueryKeys,
} from "@/lib/queries/asset-media-queries";
import { useParams } from "react-router";
import { AssetMediaDto } from "@repo/api/asset-media";
import { Input } from "@/lib/components/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/Popover";
import { AssetMediaApi } from "@/lib/api/asset-media-api";
import Toast from "@/lib/components/Toast";
import { queryClient } from "@/lib/queries/query-client";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownSelectTrigger,
} from "@/lib/components/DropdownMenu";
import { Tooltip, TooltipContent } from "@/lib/components/Tooltip";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import { SceneTypeDto } from "@repo/api/overlays";
import { MediaGrid } from "./AssetSelectorDialog/AssetSelectorDialog";
import {
  AssetSelectorBody,
  AssetSelectorBottomBar,
  AssetSelectorContent,
  AssetSelectorDialog,
  AssetSelectorDropzoneOverlay,
  AssetSelectorEmptyState,
  AssetSelectorHeader,
  AssetSelectorLayout,
  AssetSelectorMediaCard,
  AssetSelectorSidebar,
} from "./AssetSelectorDialog/AssetSelectorDialog";

export default function MediaSelectorDialog({
  sceneType,
}: {
  sceneType: SceneTypeDto;
}) {
  const isOpen = useEditorStore(
    (state) => state.overlaysTabState.mediaSelectorDialogState.isOpen,
  );
  const setIsMediaSelectorDialogOpen = useEditorStore(
    (state) => state.service.setIsMediaSelectorDialogOpen,
  );
  const addAssetMediaElements = useEditorStore(
    (state) => state.service.addAssetMediaElements,
  );
  const { teamExternalId } = useParams();
  const { data: assetMedias } = useTeamAssetMediasQuery(teamExternalId!);
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [sessionTags, setSessionTags] = useState<TagsType[]>([]);
  const [mediaTypeFilter, setMediaTypeFilter] =
    useState<MediaTypeFilter>("ALL");
  const [selectedTagsFilter, setSelectedTagsFilter] = useState<string[]>([]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    if (assetMedias) {
      assetMedias.forEach((media) => {
        if (mediaTypeFilter === "ALL" || media.type === mediaTypeFilter) {
          const mediaTags = media.metadata?.tags || [];
          mediaTags.forEach((tag: string) => tagSet.add(tag));
        }
      });
    }
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [assetMedias, mediaTypeFilter]);

  useEffect(() => {
    if (selectedTagsFilter.length === 0) return;
    const cleaned = selectedTagsFilter.filter((tag) => allTags.includes(tag));
    if (cleaned.length !== selectedTagsFilter.length) {
      setSelectedTagsFilter(cleaned);
    }
  }, [allTags, selectedTagsFilter]);

  const filteredAssetMedias = useMemo(() => {
    return assetMedias?.filter((assetMedia) => {
      if (mediaTypeFilter !== "ALL" && assetMedia.type !== mediaTypeFilter) {
        return false;
      }
      if (selectedTagsFilter.length > 0) {
        const mediaTags = assetMedia.metadata?.tags || [];
        const hasMatchingTag = mediaTags.some((tag: string) =>
          selectedTagsFilter.includes(tag),
        );
        if (!hasMatchingTag) return false;
      }
      return true;
    });
  }, [assetMedias, mediaTypeFilter, selectedTagsFilter]);

  useEffect(() => {
    if (selectedElementIds.length === 0 || !filteredAssetMedias) return;
    const visibleIds = filteredAssetMedias.map((media) => media.externalId);
    const cleaned = selectedElementIds.filter((id) => visibleIds.includes(id));
    if (cleaned.length !== selectedElementIds.length) {
      setSelectedElementIds(cleaned);
    }
  }, [filteredAssetMedias, selectedElementIds]);

  const isElementsEmpty = !assetMedias || assetMedias.length === 0;
  const hasActiveFilters =
    mediaTypeFilter !== "ALL" || selectedTagsFilter.length > 0;
  const isFilteredEmpty =
    hasActiveFilters &&
    !isElementsEmpty &&
    (!filteredAssetMedias || filteredAssetMedias.length === 0);

  const { mutate: uploadAssetMedias, isPending: isUploadingMedia } =
    useUploadAssetMediasMutation({
      teamExternalId: teamExternalId!,
      onSuccess: (uploadedMedias) => {
        setMediaTypeFilter("ALL");
        setSelectedTagsFilter([]);
        setSelectedElementIds((prev) => [
          ...prev,
          ...uploadedMedias.map((media) => media.externalId),
        ]);
      },
    });

  const handleElementClick = (elementId: string) => {
    setSelectedElementIds((prev) =>
      prev.includes(elementId)
        ? prev.filter((id) => id !== elementId)
        : [...prev, elementId],
    );
  };

  const handleAddToVideo = () => {
    if (!assetMedias || selectedElementIds.length === 0) return;

    const selectedMedias = assetMedias.filter((media) =>
      selectedElementIds.includes(media.externalId),
    );

    addAssetMediaElements(selectedMedias, sceneType);
    setSelectedElementIds([]);
    setIsMediaSelectorDialogOpen(false);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    disabled: isUploadingMedia,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "image/svg+xml": [".svg"],
      "video/mp4": [".mp4"],
      "video/quicktime": [".mov"],
      "video/webm": [".webm"],
    },
    onDrop: (acceptedFiles) => {
      if (isUploadingMedia || acceptedFiles.length === 0) {
        return;
      }
      uploadAssetMedias(acceptedFiles);
    },
  });

  return (
    <AssetSelectorDialog
      open={isOpen}
      onOpenChange={() => setIsMediaSelectorDialogOpen(false)}
    >
      <AssetSelectorLayout>
        <AssetSelectorHeader
          title="Media Library"
          isElementsEmpty={isElementsEmpty}
        />
        <AssetSelectorBody>
          <AssetSelectorSidebar isElementsEmpty={isElementsEmpty}>
            <Tabs onUploadClick={open} isUploading={isUploadingMedia} />
            <Filters
              mediaType={mediaTypeFilter}
              onMediaTypeChange={setMediaTypeFilter}
              allTags={allTags}
              selectedTagsFilter={selectedTagsFilter}
              onSelectedTagsFilterChange={setSelectedTagsFilter}
            />
          </AssetSelectorSidebar>
          <AssetSelectorContent>
            <div className="relative min-h-0 flex-1" {...getRootProps()}>
              <input {...getInputProps()} />
              {isFilteredEmpty ? (
                <AssetSelectorEmptyState
                  title="No media assets found"
                  description="Try adjusting your filters"
                />
              ) : (
                <MediaGrid<AssetMediaDto>
                  key={`${mediaTypeFilter}:${[...selectedTagsFilter].sort().join(",")}`}
                  items={filteredAssetMedias ?? []}
                  isLoading={assetMedias === undefined}
                  hideEmptyState={isDragActive}
                  emptyState={
                    <ImageUploadArea
                      getInputProps={getInputProps}
                      openImageUpload={open}
                      isDragActive={isDragActive}
                      isImagesEmpty={isElementsEmpty}
                      variant="media-dialog"
                    />
                  }
                  hasNextPage={false}
                  isFetchingNextPage={false}
                  fetchNextPage={() => {}}
                  getItemKey={(assetMedia) => assetMedia.externalId}
                  renderItem={(assetMedia) => (
                    <AssetSelectorMediaCard
                      src={assetMedia.src}
                      thumbnailSrc={assetMedia.thumbnailSrc}
                      width={assetMedia.width}
                      height={assetMedia.height}
                      tags={assetMedia.metadata?.tags}
                      isSelected={selectedElementIds.includes(
                        assetMedia.externalId,
                      )}
                      onSelect={() => handleElementClick(assetMedia.externalId)}
                    />
                  )}
                />
              )}
              {(isUploadingMedia || isDragActive) && (
                <AssetSelectorDropzoneOverlay
                  isUploadingTitle="Uploading media..."
                  description="to add it to the media library"
                  isUploading={isUploadingMedia}
                  isElementsEmpty={isElementsEmpty}
                />
              )}
            </div>
            {!isFilteredEmpty && !isElementsEmpty && (
              <AssetSelectorBottomBar
                selectedCount={selectedElementIds.length}
                onClear={() => setSelectedElementIds([])}
              >
                <DeleteMediaDialog
                  selectedExternalIds={selectedElementIds}
                  onDeleted={() => setSelectedElementIds([])}
                />
                {isOpen && (
                  <TagMediaPopover
                    assetMedias={assetMedias ?? []}
                    selectedExternalIds={selectedElementIds}
                    sessionTags={sessionTags}
                    setSessionTags={setSessionTags}
                  />
                )}
                <Button size="sm" onClick={handleAddToVideo}>
                  Add to Video
                </Button>
              </AssetSelectorBottomBar>
            )}
          </AssetSelectorContent>
        </AssetSelectorBody>
      </AssetSelectorLayout>
    </AssetSelectorDialog>
  );
}

function DeleteMediaDialog({
  selectedExternalIds,
  onDeleted,
}: {
  selectedExternalIds: string[];
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { teamExternalId } = useParams();

  const { mutate: deleteAssetMedias, isPending: isDeleting } =
    useDeleteAssetMediasMutation({
      teamExternalId: teamExternalId!,
      onSuccess: () => {
        setOpen(false);
        onDeleted();
      },
    });

  const handleDelete = () => {
    deleteAssetMedias(selectedExternalIds);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="iconSm">
          <IconTrash />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-md"
        classNameInner="flex flex-col items-center"
      >
        <DialogHeader className="w-full">
          <DialogTitle>Delete Media</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the selected media from the media
            library?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-0">
          <Button onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isDeleting}>
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Tabs({
  onUploadClick,
  isUploading,
}: {
  onUploadClick: () => void;
  isUploading: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-grey-200 p-6">
      <GradientTabButton
        icon={IconPhoto}
        label="All Media Assets"
        onClick={() => console.log("clicked")}
        isActive
      />
      <GradientTabButton
        icon={IconUpload}
        label={isUploading ? "Uploading..." : "Upload Media"}
        onClick={onUploadClick}
        isActive={false}
        disabled={isUploading}
      />
    </div>
  );
}

type MediaTypeFilter = "ALL" | "IMAGE" | "VIDEO";
const mediaTypeLabels: Record<MediaTypeFilter, string> = {
  ALL: "Media Type",
  IMAGE: "Image",
  VIDEO: "Video",
};

function Filters({
  mediaType,
  onMediaTypeChange,
  allTags,
  selectedTagsFilter,
  onSelectedTagsFilterChange,
}: {
  mediaType: MediaTypeFilter;
  onMediaTypeChange: (value: MediaTypeFilter) => void;
  allTags: string[];
  selectedTagsFilter: string[];
  onSelectedTagsFilterChange: (tags: string[]) => void;
}) {
  const hasActiveFilters = mediaType !== "ALL" || selectedTagsFilter.length > 0;

  const handleToggleTag = (tag: string) => {
    if (selectedTagsFilter.includes(tag)) {
      onSelectedTagsFilterChange(selectedTagsFilter.filter((t) => t !== tag));
    } else {
      onSelectedTagsFilterChange([...selectedTagsFilter, tag]);
    }
  };

  const handleClearAll = () => {
    onMediaTypeChange("ALL");
    onSelectedTagsFilterChange([]);
  };

  const selectedTagsLabel =
    selectedTagsFilter.length === 0
      ? "Tags"
      : selectedTagsFilter.length === 1
        ? selectedTagsFilter[0]
        : `${selectedTagsFilter.length} tags selected`;

  const isTagsEmpty = allTags.length === 0;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <h2 className="mb-5 font-semibold text-text-primary">Filters</h2>
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Select
            value={mediaType}
            onValueChange={(value) =>
              onMediaTypeChange(value as MediaTypeFilter)
            }
          >
            <SelectTrigger
              className={cn(
                "w-full",
                mediaType === "ALL" && "text-text-secondary",
              )}
            >
              <SelectValue>{mediaTypeLabels[mediaType]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Media</SelectItem>
              <SelectItem value="IMAGE">Image</SelectItem>
              <SelectItem value="VIDEO">Video</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <DropdownMenu>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div>
                <DropdownSelectTrigger
                  className={cn(
                    "w-full",
                    selectedTagsFilter.length === 0 && "text-text-secondary",
                  )}
                  disabled={isTagsEmpty}
                  iconClassName="rotate-90"
                >
                  <span className="truncate">{selectedTagsLabel}</span>
                </DropdownSelectTrigger>
              </div>
            </TooltipTrigger>
            {isTagsEmpty && (
              <TooltipContent>
                <p>No tags available. Tag media assets to filter by tags.</p>
              </TooltipContent>
            )}
          </Tooltip>
          <DropdownMenuContent
            align="start"
            className="w-(--radix-dropdown-menu-trigger-width)"
          >
            {allTags.map((tag) => (
              <DropdownMenuCheckboxItem
                key={tag}
                checked={selectedTagsFilter.includes(tag)}
                onCheckedChange={() => handleToggleTag(tag)}
                onSelect={(e) => e.preventDefault()}
                className="my-0.5"
              >
                {tag}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="mt-2 text-sm font-medium text-text-secondary"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

type TagsType = { tag: string; selectedExternalIds: string[] };

function TagMediaPopover({
  assetMedias,
  selectedExternalIds,
  sessionTags,
  setSessionTags,
}: {
  assetMedias: AssetMediaDto[];
  selectedExternalIds: string[];
  sessionTags: TagsType[];
  setSessionTags: Dispatch<SetStateAction<TagsType[]>>;
}) {
  const [open, setOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const { teamExternalId } = useParams();

  const pendingSync = useRef<TagsType[] | null>(null);
  const isSyncing = useRef(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) return;
    const tagsMap: Record<string, string[]> = {};

    assetMedias.forEach((media) => {
      const mediaTags = media.metadata?.tags || [];
      mediaTags.forEach((tag) => {
        if (!tagsMap[tag]) {
          tagsMap[tag] = [];
        }
        if (selectedExternalIds.includes(media.externalId)) {
          tagsMap[tag].push(media.externalId);
        }
      });
    });

    setSessionTags(
      Object.entries(tagsMap)
        .map(([tag, ids]) => ({
          tag,
          selectedExternalIds: ids,
        }))
        .sort((a, b) => a.tag.localeCompare(b.tag)),
    );
  };

  const syncTags = useCallback(
    async (tagsToSync: TagsType[]) => {
      if (!teamExternalId) return;

      if (isSyncing.current) {
        if (pendingSync.current) {
          const mergedMap = new Map<string, TagsType>();
          for (const tag of pendingSync.current) {
            mergedMap.set(tag.tag, tag);
          }
          for (const tag of tagsToSync) {
            mergedMap.set(tag.tag, tag);
          }
          pendingSync.current = Array.from(mergedMap.values());
        } else {
          pendingSync.current = tagsToSync;
        }
        return;
      }

      isSyncing.current = true;

      const queryKey = assetMediaQueryKeys.getTeamAssetMedias(teamExternalId);
      const previousData = queryClient.getQueryData<AssetMediaDto[]>(queryKey);

      if (previousData) {
        queryClient.setQueryData<AssetMediaDto[]>(queryKey, (old) => {
          if (!old) return old;
          return old.map((media) => {
            if (!selectedExternalIds.includes(media.externalId)) return media;

            const newTags = tagsToSync
              .filter((t) => t.selectedExternalIds.includes(media.externalId))
              .map((t) => t.tag);

            return {
              ...media,
              metadata: {
                ...media.metadata,
                tags: newTags,
              },
            };
          });
        });
      }

      try {
        await AssetMediaApi.syncAssetMediasTags(teamExternalId, {
          assetMediaExternalIds: selectedExternalIds,
          tags: tagsToSync,
        });
      } catch {
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }
        Toast.error("Failed to update tags");
      } finally {
        isSyncing.current = false;

        if (pendingSync.current) {
          const nextSync = pendingSync.current;
          pendingSync.current = null;
          syncTags(nextSync);
        }
      }
    },
    [selectedExternalIds, teamExternalId],
  );

  const handleAddNewTag = () => {
    const newTag = tagInput.trim();
    if (!newTag) return;

    const tagExists = sessionTags.some((t) => t.tag === newTag);

    let updatedTags: TagsType[];

    if (tagExists) {
      updatedTags = sessionTags.map((t) =>
        t.tag === newTag
          ? { ...t, selectedExternalIds: selectedExternalIds }
          : t,
      );
    } else {
      updatedTags = [
        { tag: newTag, selectedExternalIds: selectedExternalIds },
        ...sessionTags,
      ];
    }

    setSessionTags(updatedTags);
    setTagInput("");
    syncTags(updatedTags);
  };

  const handleToggleTag = (item: TagsType) => {
    const isSelected =
      item.selectedExternalIds.length === selectedExternalIds.length;

    const updatedTags = sessionTags.map((tag) => {
      if (tag.tag === item.tag) {
        return {
          ...tag,
          selectedExternalIds: isSelected ? [] : selectedExternalIds,
        };
      }
      return tag;
    });

    setSessionTags(updatedTags);
    syncTags(updatedTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddNewTag();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="iconSm">
          <IconTag />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="w-fit max-w-80 min-w-60 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col">
          {sessionTags.length > 0 && (
            <div className="flex max-h-54 flex-col gap-1 overflow-y-auto border-b border-grey-200 px-1.5 py-2">
              {sessionTags.map((item) => {
                const allSelected =
                  item.selectedExternalIds.length ===
                  selectedExternalIds.length;
                const someSelected =
                  item.selectedExternalIds.length > 0 && !allSelected;
                const checkboxId = `tag-checkbox-${item.tag}`;

                return (
                  <label
                    key={item.tag}
                    htmlFor={checkboxId}
                    className="flex min-w-0 cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-grey-100"
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={
                        allSelected
                          ? true
                          : someSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={() => handleToggleTag(item)}
                      className="shrink-0"
                      variant="tertiary"
                      size="lg"
                    />
                    <span className="truncate text-text-primary">
                      {item.tag}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="flex flex-row items-center gap-3 p-3">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter new tag..."
              className="h-9 w-full border-border text-sm"
            />
            <Button
              variant="outline"
              size="iconSm"
              onClick={handleAddNewTag}
              disabled={!tagInput.trim()}
              className="min-h-9 min-w-9 rounded-md disabled:opacity-100"
            >
              <IconPlus size={16} className="text-text-primary" />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
